<template>
  <div class="c-contact-us-rap">
    <component-wrapper spacing="bottom">
      <FormContainer
        :currentStep="currentStep"
        :steps="steps"
        :showProgressBar="showProgressBar"
        :title="config && config.heading"
      >
        <!-- Where is the problem -->
        <step-panel :isVisible="showLocationStep">
          <component-wrapper>
            <form-section :title="content.locationTitle">
              <div class="c-form__group">
                <div class="c-rte" v-html="content.locationDesc"></div>
              </div>
              <problem-location
                :showIsAtProperty="showIsAtProperty"
                :isAtProperty="isAtProperty"
                :isProblemAtAddressLabel="content.isProblemAtAddressLabel"
                :backButtonText="content.backButtonText"
                :continueButtonText="content.continueButtonText"
                :isLoading="isLoading"
                :locateProblemAtPropertyLabel="content.locateProblemAtPropertyLabel"
                :locateProblemOutOfPropertyTitle="
                  content.locateProblemOutOfPropertyTitle
                "
                :locateProblemOutOfPropertyDesc="
                  content.locateProblemOutOfPropertyDesc
                "
                :locateProblemAtPropertyTitle="
                  content.locateProblemAtPropertyTitle
                "
                :locateProblemAtPropertyDesc="
                  content.locateProblemAtPropertyDesc
                "
                :lookupPostcodeLabel="content.lookupPostcodeLabel"
                :lookupCustomerReferenceLabel="
                  content.lookupCustomerReferenceLabel
                "
                :lookupPostcodeDesc="content.lookupPostcodeDesc"
                :searchButtonLabel="content.searchButtonLabel"
                :postcodeNotListed="content.postcodeNotListed"
                :postcodeNotAllowed="content.postcodeNotAllowed"
                :customerReferenceNotListed="content.customerReferenceNotListed"
                :customerReferenceNotAllowed="
                  content.customerReferenceNotAllowed
                "
                :customersOnly="config.customersOnly"
                @isAtPropertyChange="isAtPropertyChange"
                @updateAddress="confirmLocation"
                @updateCustomerReference="updateCustomerReference"
                @stepChange="stepChange(1)"
              />
            </form-section>
          </component-wrapper>
        </step-panel>

        <!-- Step 1 - Dynamic questions -->
        <step-panel :isVisible="showStepOne">
          <component-wrapper>
            <form-section
              :title="content.stepOneTitle"
              :desc="content.stepOneDesc"
            >
            </form-section>

            <div
              class="c-form__group"
              v-for="(question, index) in wizard"
              :key="question.id"
              :id="question.id"
            >
              <component
                :index="index"
                :is="question.type"
                :question="question"
                :activeTooltipId="activeTooltip"
                @hook:mounted="scrollToComponent(question.id)"
                @input="updateQuestions"
                @activeTooltip="setActiveTooltip"
              >
              </component>
            </div>

            <div v-if="offerUpload && isFinalQuestion" class="c-form__group">
              <upload-box
                :extensions="extensions"
                :uploadPostAction="fileUploadAction"
                :title="content.uploadTitle"
                :intro="content.uploadIntro"
                @resetFile="resetFile"
                @fileUpload="setFileId"
                @isUploadingFile="setUploadingFile"
              />
            </div>
            
            <action-buttons
              :prevLabel="content.backButtonText"
              :nextLabel="content.continueButtonText"
              @onPrev="stepChange(0)"
              @onNext="collateAnswers()"
              :nextDisabled="stepTwoIsDisabled"
            />
          </component-wrapper>
        </step-panel>

        <!-- Step 2 - User details -->
        <step-panel :isVisible="showStepTwo">
          <component-wrapper>
            <form-section
              :title="content.stepTwoTitle"
              :desc="content.stepTwoDesc"
            >
            </form-section>
          </component-wrapper>
          <component-wrapper spacing="bottom">
            <user-details
              :aboutYouEmailLabel="content.aboutYouEmailLabel"
              :aboutYouConfirmEmailLabel="content.aboutYouConfirmEmailLabel"
              :aboutYouFirstNameLabel="content.aboutYouFirstNameLabel"
              :aboutYouOtherTitleLabel="content.aboutYouOtherTitleLabel"
              :aboutYouPrimaryContactLabel="content.aboutYouPrimaryContactLabel"
              :aboutYouPrimaryContactToolTip="
                content.aboutYouPrimaryContactToolTip
              "
              :aboutYouEmailToolTip="content.aboutYouEmailToolTip"
              :aboutYouSecondaryContactLabel="
                content.aboutYouSecondaryContactLabel
              "
              :aboutYouSurnameLabel="content.aboutYouSurnameLabel"
              :aboutYouCustomerReferenceNumberLabel="
                content.aboutYouCustomerReferenceNumberLabel
              "
              :aboutYouTitleLabel="content.aboutYouTitleLabel"
              :aboutYouCustomerReferenceNumberTooltip="
                content.aboutYouCustomerReferenceNumberTooltip
              "
              :aboutYouAdditionalNeedsTooltip="
                content.aboutYouAdditionalNeedsTooltip
              "
              :aboutYouAdditionalNeedsLabel="
                content.aboutYouAdditionalNeedsLabel
              "
              :aboutYouAdditionalNeedsDesc="content.aboutYouAdditionalNeedsDesc"
              :email="email"
              :confirmEmail="confirmEmail"
              :firstName="firstName"
              :surname="surname"
              :customerReferenceNumber="customerReferenceNumber"
              :additionalNeeds="additionalNeeds"
              :otherTitle="otherTitle"
              :primaryContact="primaryContact"
              :secondaryContact="secondaryContact"
              :primaryContactType="primaryContactType"
              :secondaryContactType="secondaryContactType"
              :title="title"
              :activeTooltipId="activeTooltip"
              :backButtonText="content.backButtonText"
              :continueButtonText="content.continueButtonText"
              @collateDetails="collateDetails"
              @onPrev="stepChange(1)"
              @valueChange="formBoxValueChange"
              @activeTooltip="setActiveTooltip"
            >
            </user-details>
          </component-wrapper>
        </step-panel>

        <!-- Step 3 - Review -->
        <step-panel :isVisible="showStepThree">
          <component-wrapper>
            <form-section
              :title="content.stepThreeTitle"
              :desc="content.stepThreeDesc"
            >
              <form-review
                :sections="formSections"
                @editSection="handleSectionEdit"
              />
            </form-section>
          </component-wrapper>
          <component-wrapper>
            <action-buttons
              :prevLabel="content.backButtonText"
              :nextLabel="content.submitButtonText"
              :nextType="submit"
              :nextDisabled="isLoading"
              :nextLoading="isLoading"
              @onPrev="stepChange(2)"
              @onNext="postIncidentReport"
            />
          </component-wrapper>
        </step-panel>

        <!-- Thank you -->
        <step-panel :isVisible="showStepFour">
          <component-wrapper spacing="bottom">
            <form-section
              :title="confirmationTitle"
              :desc="confirmationMessage"
            >
              <div>
                <div v-if="confirmationReference">
                  <strong
                    >Incident Number:
                    <span>
                      {{ confirmationReference }}
                    </span>
                  </strong>
                  <div
                    v-if="signupEnabled && !isLoggedIn"
                    class="c-contact-us-rap__signup c-bordered-content"
                  >
                    <form action="/RapSignUp" method="post">
                      <h3 class="c-contact-us-rap__heading">
                        {{ content.createAccount.title }}
                      </h3>
                      <div v-html="content.createAccount.text"></div>
                      <input
                        type="hidden"
                        name="IncidentId"
                        :value="confirmationReference"
                      />
                      <input
                        type="hidden"
                        name="Email"
                        :value="formBoxData.email"
                      />
                      <input
                        type="hidden"
                        name="Title"
                        :value="formBoxData.title"
                      />
                      <input
                        type="hidden"
                        name="OtherTitle"
                        :value="formBoxData.otherTitle"
                      />
                      <input
                        type="hidden"
                        name="Firstname"
                        :value="formBoxData.firstName"
                      />
                      <input
                        type="hidden"
                        name="Lastname"
                        :value="formBoxData.surname"
                      />
                      <input
                        type="hidden"
                        name="Postcode"
                        :value="address.propertyPostcode"
                      />
                      <input
                        type="hidden"
                        name="PrimaryContactNumber"
                        :value="formBoxData.primaryContact"
                      />
                      <button
                        type="submit"
                        class="c-button c-button--brand-alt c-contact-us-rap__button"
                      >
                        {{ content.createAccount.buttonText }}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </form-section>
          </component-wrapper>
        </step-panel>
      </FormContainer>
    </component-wrapper>
  </div>
</template>

<script>
  import ActionButtons from "../../forms/action-buttons/ActionButtons.vue";
  import axios from "axios";
  import endpoints from "Globals/mando.endpoints";
  import jump from "jump.js";
  import FormContainer from "../../forms/form-container/form-container.vue";
  import FormReview from "../../form-review/FormReview.vue";

  import BaseButton from "./BaseButton";
  import ComponentWrapper from "./ComponentWrapper";
  import ConfirmationBox from "./ConfirmationBox";
  import FormInput from "./FormInput";
  import FormSelect from "./FormSelect";
  import FormSection from "./FormSection";
  import ProblemLocation from "./ProblemLocation";
  import RadioGroupOptions from "./RadioGroupOptions";
  import RadioListOptions from "./RadioListOptions";
  import StepPanel from "./StepPanel";
  import SummaryBox from "./SummaryBox";
  import TextareaBox from "./TextareaBox";
  import UploadBox from "./UploadBox";
  import UserDetails from "./UserDetails";

  import Vue from "NodeModules/vue/dist/vue.min.js";

  export default {
    components: {
      ActionButtons,
      BaseButton,
      ComponentWrapper,
      ConfirmationBox,
      FormContainer,
      FormInput,
      FormReview,
      FormSelect,
      FormSection,
      ProblemLocation,
      RadioGroupOptions,
      RadioListOptions,
      StepPanel,
      SummaryBox,
      TextareaBox,
      UploadBox,
      UserDetails,
    },
    props: {
      componentId: {
        type: String,
        required: true,
      },
      config: {
        type: Object,
        default: false,
      },
      content: {
        required: true,
        type: Object,
      },
      questions: {
        required: true,
        type: Array,
      },
      pathId: {
        required: true,
        type: String,
      },
      journey: {
        type: String,
        required: false,
        default: null,
      },
      user: {
        type: Object,
      },
    },
    data() {
      return {
        address: {
          id: "",
          label: "",
          wessexProvider: "",
        },
        answers: [],
        details: [],
        confirmationMessage: "",
        confirmationReference: "",
        confirmationTitle: "",
        contactDetailsRequestError: false,
        currentStep: 0,
        extensions: [
          "jpeg",
          "jpe",
          "jpg",
          "png",
          "heic",
          "hdr",
          "mp4",
          "mpg",
          "mpeg",
          "mkv",
          "mov",
          "avi",
        ],
        formBoxData: {
          email: "",
          confirmEmail: "",
          firstName: "",
          otherTitle: "",
          primaryContact: "",
          secondaryContact: "",
          primaryContactType: "",
          secondaryContactType: "",
          customerReferenceNumber: "",
          additionalNeeds: "",
          surname: "",
          title: "",
        },
        fileUploadId: "",
        fileUploadName: "",
        isLoading: false,
        isLoggedIn: false,
        isFormModified: false,
        notFound: false,
        isAtProperty: "",
        isFinalQuestion: false,
        isUploadingFile: false,
        offerUpload: false,
        showSummary: false,
        signupEnabled: false,
        activeTooltip: "",
        wizard: [],
        steps: [
          {
            label: 1,
          },
          {
            label: 2,
          },
          {
            label: 3,
          },
        ],
      };
    },
    mounted() {
      if (this.config) {
        const question = this.questions.find(
          (option) => option.id === this.config.id
        );
        this.wizard.push(question);

        this.isAtProperty = this.config.isProblemAtAddress;
      } else {
        this.wizard.push(this.questions[0]);
      }
    },
    created() {
      for (const key in this.user) {
        this.formBoxData[key] = this.user[key];
      }
    },
    watch: {
      "formBoxData.title"(value) {
        if (value !== "Other") {
          this.formBoxData.otherTitle = "";
        }
      },
      offerUpload(value) {
        this.resetFile();
        if (!value) {
          this.isUploadingFile = false;
        }
      },
    },
    computed: {
      stepTwoIsDisabled() {
        const invalid = this.wizard.find(
          (field) => field.answerLabel === "" && field.mandatory === true
        );
        return !!invalid || !this.isFinalQuestion || this.isUploadingFile;
      },
      fileUploadAction() {
        return endpoints("postIncidentUpload");
      },
      showIsAtProperty() {
        return !this.config || !this.config.isProblemAtAddress.length;
      },
      showLocationNext() {
        return this.isAtProperty === "Yes";
      },
      currentQuestionIndex() {
        return this.wizard.length - 1;
      },
      showProgressBar() {
        return this.currentStep > 0 && this.currentStep <= this.steps.length;
      },
      showLocationStep() {
        return this.currentStep === 0;
      },
      showStepOne() {
        return this.currentStep === 1;
      },
      showStepTwo() {
        return this.currentStep === 2;
      },
      showStepThree() {
        return this.currentStep === 3;
      },
      showStepFour() {
        return this.currentStep === 4;
      },
      uploadEnable() {
        return this.wizard.find((o) => o.upload === true) ? true : false;
      },
      formSections() {
        const reviewLabel =
          this.isAtProperty === "Yes"
            ? this.content.locateProblemAtPropertyTitle
            : this.content.locateProblemOutOfPropertyTitle;
        return [
          {
            heading: this.content.locationTitle,
            sectionId: "entry",
            items: [
              {
                title: this.content.isProblemAtAddressLabel,
                description: this.isAtProperty,
              },
              {
                title: reviewLabel,
                description: this.address.label,
              },
            ],
          },
          {
            heading: this.content.stepOneTitle,
            sectionId: "stepOne",
            items: this.stepTwoReview(this.wizard),
          },
          {
            heading: this.content.stepTwoTitle,
            sectionId: "stepTwo",
            items: this.details.map((detail) => {
              return {
                title: detail.label,
                description: detail.answerLabel,
              };
            }),
          },
        ];
      },
      title() {
        return this.formBoxData.title;
      },
      otherTitle() {
        return this.formBoxData.otherTitle;
      },
      email() {
        return this.formBoxData.email;
      },
      confirmEmail() {
        return this.formBoxData.confirmEmail;
      },
      firstName() {
        return this.formBoxData.firstName;
      },
      surname() {
        return this.formBoxData.surname;
      },
      customerReferenceNumber() {
        return this.formBoxData.customerReferenceNumber;
      },
      additionalNeeds() {
        return this.formBoxData.additionalNeeds;
      },
      primaryContact() {
        return this.formBoxData.primaryContact;
      },
      secondaryContact() {
        return this.formBoxData.secondaryContact;
      },
      primaryContactType() {
        return this.formBoxData.primaryContactType;
      },
      secondaryContactType() {
        return this.formBoxData.secondaryContactType;
      },
    },
    methods: {
      stepTwoReview(questions) {
        const fileUpload = {
          title: this.content.uploadTitle,
          description: this.fileUploadName,
        };

        const answers = questions.map((question) => ({
          title: question.label,
          description: question.answerLabel,
        }));
        return [...answers, fileUpload];
      },
      updateCustomerReference(value) {
        this.formBoxData.customerReferenceNumber = value;
      },
      resetFile() {
        this.fileUploadId = "";
        this.fileUploadName = "";
      },
      setUploadingFile(value) {
        this.isUploadingFile = value;
      },
      confirmLocation(address) {
        this.address = address;
      },
      isAtPropertyChange(value) {
        this.isAtProperty = value;
      },
      handleSectionEdit(id, index) {
        this.stepChange(index);
      },
      collateAnswers() {
        this.answers = this.wizard.map((question) => {
          return {
            id: question.id,
            answer: question.answerId,
            uploadEnable: question.uploadEnable,
          };
        });

        this.stepChange(2);
      },
      collateDetails() {
        this.details = [
          {
            answerLabel: this.formBoxData.title,
            label: this.content.aboutYouTitleLabel,
          },
          {
            answerLabel: this.formBoxData.otherTitle,
            label: this.content.aboutYouOtherTitleLabel,
          },
          {
            answerLabel: this.formBoxData.firstName,
            label: this.content.aboutYouFirstNameLabel,
          },
          {
            answerLabel: this.formBoxData.surname,
            label: this.content.aboutYouSurnameLabel,
          },
          {
            answerLabel: this.formBoxData.customerReferenceNumber,
            label: this.content.aboutYouCustomerReferenceNumberLabel,
          },
          {
            answerLabel: this.formBoxData.email,
            label: this.content.aboutYouEmailLabel,
          },
          {
            answerLabel: this.formBoxData.confirmEmail,
            label: this.content.aboutYouConfirmEmailLabel,
          },
          {
            answerLabel: this.formBoxData.primaryContact,
            label: this.content.aboutYouPrimaryContactLabel,
          },
          {
            answerLabel: this.formBoxData.primaryContactType,
            label: `${this.content.aboutYouPrimaryContactLabel} type`,
          },
          {
            answerLabel: this.formBoxData.secondaryContact,
            label: this.content.aboutYouSecondaryContactLabel,
          },
          {
            answerLabel: this.formBoxData.secondaryContactType,
            label: `${this.content.aboutYouSecondaryContactLabel} type`,
          },
          {
            answerLabel: this.formBoxData.additionalNeeds,
            label: this.content.aboutYouAdditionalNeedsLabel,
          },
        ];
        this.stepChange(3);
      },
      formBoxValueChange(key, value) {
        this.formBoxData[key] = value;
      },
      updateQuestions(value, index, questionId, nextId, isFinal, offerUpload) {
        const currentWizardItem = this.wizard.find(
          (item) => item.id === questionId
        );
        const isTextarea = currentWizardItem.type === "textarea-box";

        if (
          value === "" &&
          isTextarea &&
          currentWizardItem.hasOwnProperty("answerLabel")
        ) {
          this.handleEmptyTextarea(index, currentWizardItem);
          return;
        }

        this.offerUpload = offerUpload;

        if (isTextarea) {
          currentWizardItem.answerLabel = value;
        } else {
          this.updateWizardItem(currentWizardItem, value, isFinal, index);
        }

        if (index < this.wizard.length - 1) {
          this.resetFileUploadState(index);
        }

        if (nextId) {
          this.addNextQuestion(index, nextId);
        }

        this.isFinalQuestion = this.checkFinalQuestion();
      },
      handleEmptyTextarea(index, item) {
        const nextSteps = this.wizard.slice(index + 1);

        if (item.answerLabel.length > 0 || item.answerLabel === "") {
          item.answerLabel = "";
          const updatedItem = Object.assign({}, item);
          Vue.set(this.wizard, index, updatedItem);

          if (nextSteps && nextSteps.length > 0) {
            const updatedFlow = this.wizard
              .slice(0, index + 1)
              .concat(nextSteps);
            this.wizard = updatedFlow;
          }
        }
      },
      updateWizardItem(wizardItem, value, isFinal, index) {
        wizardItem.final = !!isFinal;
        wizardItem.answerId = this.lookupProp(value, index, "optionId");
        wizardItem.answerLabel = this.lookupProp(value, index, "label");
        wizardItem.customerOnly = this.lookupProp(value, index, "customerOnly");
        wizardItem.uploadEnable = this.lookupProp(value, index, "upload");
      },
      resetFileUploadState(index) {
        this.offerUpload = false;

        const nextSteps = this.wizard.splice(index + 1);
        const updatedFlow = Object.assign({}, nextSteps);
        Vue.set(this.wizard, index + 1, updatedFlow);
      },
      addNextQuestion(index, nextId) {
        const foundQuestion = this.questions.find(
          (question) => question.id === nextId
        );
        if (foundQuestion) {
          foundQuestion.answerLabel = "";
          Vue.set(this.wizard, index + 1, foundQuestion);
        }
      },
      checkFinalQuestion() {
        const currentQuestion = this.wizard[this.currentQuestionIndex];
        return Boolean(
          currentQuestion.final &&
            currentQuestion.answerLabel &&
            currentQuestion.answerLabel.length > 0
        );
      },
      lookupProp(value, questionIndex, prop) {
        if (
          ["radio-list-options", "radio-group-options"].includes(
            this.wizard[questionIndex].type
          )
        ) {
          return this.wizard[questionIndex].options.find(
            (opt) => opt.value === value
          )[prop];
        }
      },
      postIncidentReport() {
        this.isLoading = true;

        const payload = {
          componentId: this.componentId,
          title: this.formBoxData.title,
          otherTitle: this.formBoxData.otherTitle,
          firstName: this.formBoxData.firstName,
          surname: this.formBoxData.surname,
          customerReferenceNumber: this.formBoxData.customerReferenceNumber,
          additionalNeeds: this.formBoxData.additionalNeeds,
          primaryContact: this.formBoxData.primaryContact,
          secondaryContact: this.formBoxData.secondaryContact,
          primaryContactType: this.formBoxData.primaryContactType,
          secondaryContactType: this.formBoxData.secondaryContactType,
          email: this.formBoxData.email,
          confirmEmail: this.formBoxData.confirmEmail,
          addressId: this.address.value,
          answers: (this.answers = [...this.wizard]
            .reverse()
            .map(({ id, answerId, answerLabel }) => {
              return {
                id: id,
                answer: answerId ? answerId : answerLabel,
              };
            })),
          fileUploadId: this.fileUploadId,
          problemIsAtProperty: this.isAtProperty,
        };

        if (this.journey) {
          payload.journey = this.journey;
        }

        axios
          .post(`${endpoints("postIncidentReport")}`, payload)
          .then(this.postIncidentReportSuccess)
          .catch(this.postIncidentReportFail)
          .finally(this.postIncidentReportFinally);
      },

      postIncidentReportFail() {
        this.stepChange(4);
        this.isLoading = false;
        this.confirmationTitle = "Submission Error";
        this.confirmationMessage =
          "There was an error during submission. Please try again later.";
      },
      postIncidentReportFinally() {
        this.isLoading = false;
        this.showSummary = false;
      },
      postIncidentReportSuccess(r) {
        if (!r.data.error) {
          this.confirmationMessage = r.data.results.message;
          this.confirmationReference = r.data.results.reference;
          this.confirmationTitle = r.data.results.title;
          this.isLoggedIn = r.data.results.isLoggedIn;
        } else {
          this.confirmationTitle = "Submission Error";
          this.confirmationMessage = r.data.errorText;
        }
        this.stepChange(4);
      },
      resetDetails() {
        this.details = [];
        this.showSummary = false;
      },
      setActiveTooltip(id) {
        this.activeTooltip = id;
      },
      stepChange(step, toggleSummary) {
        this.currentStep = step;
        jump(".c-form-container");

        if (toggleSummary) {
          this.toggleSummary();
        }
      },
      scrollToComponent(id) {
        const el = document.getElementById(id);
        setTimeout(() => {
          el.scrollIntoView({
            behavior: "smooth",
            align: "false",
            block: "end",
            inline: "nearest",
          });
        }, 300);
      },
      setFileId(e, file) {
        this.fileUploadName = file.name;
        this.isUploadingFile = false;
        this.fileUploadId = e.results.id;
      },
      toggleSummary() {
        this.showSummary = !this.showSummary;
        if (this.showSummary === true) {
          jump(".c-form-container");
        }
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/components/reportAProblem/_contact-us-rap/_vue/ReportProblem.vue